<template>
  <!-- Floating launcher so NON-ADMIN roles (teacher, para, case manager …) can
       run the smoke suite: they can't open Admin → System Diagnostics, but
       per-role coverage requires running the suite signed in as those roles.
       Dev app only — hardened builds resolve this to a null stub.
       Also opens from Student Management header via `casemanage:open-smoke-test`. -->
  <div v-if="available && signedIn">
    <button
      v-if="!open"
      class="smoke-launcher-fab"
      title="Run smoke test as the current signed-in role (Ctrl+Shift+S). Dev app only."
      @click="open = true"
    >
      <Bot :size="16" />
      <span>Smoke test</span>
    </button>

    <div
      v-if="open"
      class="smoke-launcher-overlay"
      @mousedown.self="onBackdropPointerDown"
      @click.self="onBackdropClick($event, () => (open = false))"
    >
      <div class="smoke-launcher-modal">
        <div class="modal-header">
          <h3><Bot :size="18" class="inline-icon" /> Smoke Test Runner</h3>
          <span class="modal-role">
            signed in as {{ currentUser?.email }} (JWT {{ jwtRole || 'no role' }})
          </span>
          <button class="modal-close" aria-label="Close" @click="open = false">
            <X :size="18" />
          </button>
        </div>
        <div class="modal-body">
          <SmokeTestRunnerPanel />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useBackdropDismiss } from '@/composables/useBackdropDismiss'
import { Bot, X } from 'lucide-vue-next'
import { useAuthStore } from '@/store/authStore'
import SmokeTestRunnerPanel from './SmokeTestRunnerPanel.vue'
import { useDebugToolsSettings } from '@/composables/useDebugToolsSettings'
import { loadSmokeAuthInspection } from './authContext.js'

const OPEN_EVENT = 'casemanage:open-smoke-test'

const { smokeTestVisible } = useDebugToolsSettings()
const available = smokeTestVisible
const authStore = useAuthStore()
const currentUser = computed(() => authStore.currentUser)
const signedIn = computed(() => !!currentUser.value?.uid)
const jwtRole = ref(null)

const open = ref(false)

async function refreshJwtRole() {
  try {
    const loaded = await loadSmokeAuthInspection(currentUser.value)
    jwtRole.value = loaded.inspection.jwtRole
  } catch {
    jwtRole.value = null
  }
}

const { onBackdropPointerDown, onBackdropClick } = useBackdropDismiss()

function handleKeydown(event) {
  if (event.ctrlKey && event.shiftKey && event.key === 'S') {
    event.preventDefault()
    open.value = !open.value
  }
}

function handleOpenEvent() {
  open.value = true
}

watch(open, (isOpen) => {
  if (isOpen) refreshJwtRole()
})

onMounted(() => {
  // Clear legacy permanent-hide flag from older builds that stored this.
  try {
    localStorage.removeItem('smoke-launcher-hidden')
  } catch {
    /* ignore */
  }
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener(OPEN_EVENT, handleOpenEvent)
  refreshJwtRole()
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener(OPEN_EVENT, handleOpenEvent)
})
</script>

<style scoped>
.smoke-launcher-fab {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 10050;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #1e3a8a;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.smoke-launcher-fab:hover {
  background: #1d4ed8;
}
.smoke-launcher-overlay {
  position: fixed;
  inset: 0;
  z-index: 10060;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.smoke-launcher-modal {
  background: var(--bg-primary, #fff);
  border-radius: 10px;
  width: min(760px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border-color, #e5e5e5);
}
.modal-header h3 {
  margin: 0;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.modal-role {
  flex: 1;
  font-size: 12px;
  color: var(--text-secondary, #777);
}
.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #666);
  display: inline-flex;
}
.modal-body {
  padding: 16px 18px;
  overflow-y: auto;
}
</style>
